import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { OwnerBookingListItemDto } from "../types/conversations";

type LoadState = "loading" | "ready" | "error";

function formatDate(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTimeRange(
  startTime?: string | null,
  endTime?: string | null
): string {
  if (!startTime && !endTime) return "—";
  if (startTime && endTime) return `${startTime.slice(0, 5)} – ${endTime.slice(0, 5)}`;
  if (startTime) return startTime.slice(0, 5);
  if (endTime) return endTime.slice(0, 5);
  return "—";
}

function formatMoney(amountPence?: number | null, currency?: string | null): string {
  const pence = typeof amountPence === "number" ? amountPence : 0;
  const code = (currency || "gbp").toUpperCase();

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: code,
  }).format(pence / 100);
}

function normalizeStatus(status?: string | null): string {
  return (status || "").trim().toLowerCase();
}

function formatStatus(status?: string | null): string {
  const normalized = normalizeStatus(status);
  if (normalized === "checkoutcreated") return "Payment pending";
  if (!status?.trim()) return "Unknown";
  return status;
}

function buildWalkerName(item: OwnerBookingListItemDto): string {
  const full = [item.walkerFirstName, item.walkerLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return item.walkerDisplayTitle || full || "Walker";
}

const OwnerBookingsPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>("loading");
  const [items, setItems] = useState<OwnerBookingListItemDto[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      if (!token?.trim()) {
        setError("Secure owner token is missing.");
        setState("error");
        return;
      }

      try {
        const result = await walkiesClient.getOwnerBookingsByToken(token);
        setItems(result);
        setState("ready");
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Unable to load owner bookings."
        );
        setState("error");
      }
    }

    void load();
  }, [token]);

  const grouped = useMemo(() => {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(
      2,
      "0"
    )}-${`${today.getDate()}`.padStart(2, "0")}`;

    const sorted = [...items].sort((a, b) => {
      const aDate = a.serviceDateLocal || "";
      const bDate = b.serviceDateLocal || "";

      if (aDate !== bDate) return bDate.localeCompare(aDate);

      const aTime = a.startTimeLocal || "";
      const bTime = b.startTimeLocal || "";
      if (aTime !== bTime) return bTime.localeCompare(aTime);

      return b.bookingProposalId - a.bookingProposalId;
    });

    const upcoming: OwnerBookingListItemDto[] = [];
    const paid: OwnerBookingListItemDto[] = [];
    const completed: OwnerBookingListItemDto[] = [];
    const cancelled: OwnerBookingListItemDto[] = [];

    for (const item of sorted) {
      const status = normalizeStatus(item.status);
      const isTodayOrFuture = !item.serviceDateLocal || item.serviceDateLocal >= todayKey;

      if (status === "completed") {
        completed.push(item);
        continue;
      }

      if (status === "cancelled") {
        cancelled.push(item);
        continue;
      }

      if (
        isTodayOrFuture &&
        (status === "paid" || status === "sent" || status === "checkoutcreated")
      ) {
        upcoming.push(item);
        continue;
      }

      if (status === "paid" || status === "sent" || status === "checkoutcreated") {
        paid.push(item);
      }
    }

    return { upcoming, paid, completed, cancelled };
  }, [items]);

  function openConversation(item: OwnerBookingListItemDto) {
    if (!item.ownerReplyToken?.trim()) return;

    const qs = new URLSearchParams();
    if (item.walkerSlug?.trim()) qs.set("walkerSlug", item.walkerSlug.trim());
    if (item.walkerDisplayTitle?.trim()) qs.set("walkerName", item.walkerDisplayTitle.trim());

    navigate(`/reply/${item.ownerReplyToken.trim()}${qs.toString() ? `?${qs.toString()}` : ""}`);
  }

  function rebookSameWalker(item: OwnerBookingListItemDto) {
    if (!item.ownerReplyToken?.trim()) return;

    const qs = new URLSearchParams();
    qs.set("rebook", String(item.bookingProposalId));

    if (item.walkerSlug?.trim()) qs.set("walkerSlug", item.walkerSlug.trim());
    if (item.walkerDisplayTitle?.trim()) qs.set("walkerName", item.walkerDisplayTitle.trim());

    navigate(`/reply/${item.ownerReplyToken.trim()}?${qs.toString()}`);
  }

  function findAnotherWalker(item: OwnerBookingListItemDto) {
    const qs = new URLSearchParams();
    qs.set("from", "rebook");

    if (item.walkerDisplayTitle?.trim()) {
      qs.set("previousWalker", item.walkerDisplayTitle.trim());
    }

    navigate(`/walkers?${qs.toString()}`);
  }

  if (state === "loading") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Loading your bookings…</h1>
          </div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <h1 style={styles.title}>Unable to load your bookings</h1>
            <p style={styles.textMuted}>{error}</p>
            <div style={styles.actions}>
              <Link to="/reply-access" style={styles.primaryLink}>
                Request fresh access links
              </Link>
              <Link to="/walkers" style={styles.secondaryLink}>
                Browse walkers
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const total =
    grouped.upcoming.length +
    grouped.paid.length +
    grouped.completed.length +
    grouped.cancelled.length;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.badge}>Owner bookings</div>
          <h1 style={styles.title}>Your bookings</h1>
          <p style={styles.textMuted}>
            View past and current bookings, reopen conversations, book the same walker again, or find someone new.
          </p>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Upcoming</div>
              <div style={styles.summaryValue}>{grouped.upcoming.length}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Paid</div>
              <div style={styles.summaryValue}>{grouped.paid.length}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Completed</div>
              <div style={styles.summaryValue}>{grouped.completed.length}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Cancelled</div>
              <div style={styles.summaryValue}>{grouped.cancelled.length}</div>
            </div>
          </div>

          {total === 0 ? (
            <div style={styles.emptyBox}>
              No bookings found yet for this owner access link.
            </div>
          ) : (
            <>
              <BookingSection
                title="Upcoming bookings"
                items={grouped.upcoming}
                emptyText="No upcoming bookings."
                onOpenConversation={openConversation}
                onRebook={rebookSameWalker}
                onFindAnother={findAnotherWalker}
              />

              <BookingSection
                title="Paid bookings"
                items={grouped.paid}
                emptyText="No paid bookings."
                onOpenConversation={openConversation}
                onRebook={rebookSameWalker}
                onFindAnother={findAnotherWalker}
              />

              <BookingSection
                title="Completed bookings"
                items={grouped.completed}
                emptyText="No completed bookings."
                onOpenConversation={openConversation}
                onRebook={rebookSameWalker}
                onFindAnother={findAnotherWalker}
              />

              <BookingSection
                title="Cancelled bookings"
                items={grouped.cancelled}
                emptyText="No cancelled bookings."
                onOpenConversation={openConversation}
                onRebook={rebookSameWalker}
                onFindAnother={findAnotherWalker}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

type BookingSectionProps = {
  title: string;
  items: OwnerBookingListItemDto[];
  emptyText: string;
  onOpenConversation: (item: OwnerBookingListItemDto) => void;
  onRebook: (item: OwnerBookingListItemDto) => void;
  onFindAnother: (item: OwnerBookingListItemDto) => void;
};

const BookingSection: React.FC<BookingSectionProps> = ({
  title,
  items,
  emptyText,
  onOpenConversation,
  onRebook,
  onFindAnother,
}) => {
  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.countBubble}>{items.length}</div>
      </div>

      {items.length === 0 ? (
        <div style={styles.emptyRow}>{emptyText}</div>
      ) : (
        <div style={styles.list}>
          {items.map((item) => (
            <div key={item.bookingProposalId} style={styles.bookingCard}>
              <div style={styles.bookingTopRow}>
                <div>
                  <div style={styles.bookingTitle}>
                    {item.serviceType || "Booking proposal"}
                  </div>
                  <div style={styles.bookingWalker}>
                    Walker: {buildWalkerName(item)}
                  </div>
                </div>

                <div style={styles.statusBadge}>{formatStatus(item.status)}</div>
              </div>

              <div style={styles.detailGrid}>
                <DetailItem label="Date" value={formatDate(item.serviceDateLocal)} />
                <DetailItem label="Time" value={formatTimeRange(item.startTimeLocal, item.endTimeLocal)} />
                <DetailItem label="Amount" value={formatMoney(item.grossAmountPence, item.currency)} />
                <DetailItem label="Location" value={item.locationSummary || "—"} />
                <DetailItem label="Dogs" value={item.dogCount ? String(item.dogCount) : "—"} />
                <DetailItem label="Created" value={formatDateTime(item.createdUtc)} />
              </div>

              {item.notes && <div style={styles.notesBox}>{item.notes}</div>}

              <div style={styles.buttonRow}>
                {item.ownerReplyToken?.trim() && (
                  <button style={styles.primaryButton} onClick={() => onOpenConversation(item)}>
                    Open conversation
                  </button>
                )}

                {item.ownerReplyToken?.trim() && (
                  <button style={styles.secondaryButton} onClick={() => onRebook(item)}>
                    Book this walker again
                  </button>
                )}

                <button style={styles.secondaryButton} onClick={() => onFindAnother(item)}>
                  Find another walker
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

type DetailItemProps = {
  label: string;
  value: string;
};

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => {
  return (
    <div style={styles.detailItem}>
      <div style={styles.detailLabel}>{label}</div>
      <div style={styles.detailValue}>{value}</div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242,138,22,0.16), transparent 28%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
    padding: "32px 16px 64px",
    color: "#fff",
  },
  container: {
    maxWidth: 1100,
    margin: "0 auto",
  },
  card: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
  },
  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47,79,70,0.08)",
    fontWeight: 800,
    marginBottom: 12,
  },
  title: {
    margin: 0,
    fontSize: 38,
    fontWeight: 900,
    color: "#213a33",
  },
  textMuted: {
    margin: "12px 0 0",
    fontSize: 15,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.6,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginTop: 24,
  },
  summaryCard: {
    borderRadius: 20,
    padding: 16,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.08)",
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: "rgba(24, 32, 29, 0.72)",
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 900,
    color: "#213a33",
    marginTop: 8,
  },
  section: {
    marginTop: 28,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 14,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 900,
    color: "#213a33",
  },
  countBubble: {
    minWidth: 36,
    height: 36,
    padding: "0 12px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    background: "rgba(33, 58, 51, 0.08)",
    color: "#213a33",
  },
  emptyBox: {
    marginTop: 24,
    borderRadius: 18,
    padding: 18,
    background: "rgba(33, 58, 51, 0.03)",
    border: "1px dashed rgba(33, 58, 51, 0.14)",
    color: "rgba(24, 32, 29, 0.72)",
  },
  emptyRow: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(33, 58, 51, 0.03)",
    border: "1px dashed rgba(33, 58, 51, 0.14)",
    color: "rgba(24, 32, 29, 0.72)",
  },
  list: {
    display: "grid",
    gap: 16,
  },
  bookingCard: {
    borderRadius: 22,
    padding: 20,
    background: "#fff",
    border: "1px solid rgba(33, 58, 51, 0.08)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.05)",
  },
  bookingTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  bookingTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: "#213a33",
  },
  bookingWalker: {
    marginTop: 6,
    fontSize: 14,
    color: "rgba(24, 32, 29, 0.72)",
  },
  statusBadge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 13,
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#b45309",
  },
  detailGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  detailItem: {
    padding: 12,
    borderRadius: 16,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.06)",
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "rgba(24, 32, 29, 0.55)",
  },
  detailValue: {
    marginTop: 6,
    fontSize: 15,
    fontWeight: 700,
    color: "#213a33",
    lineHeight: 1.4,
  },
  notesBox: {
    marginTop: 16,
    padding: 14,
    borderRadius: 16,
    background: "rgba(242, 138, 22, 0.06)",
    border: "1px solid rgba(242, 138, 22, 0.12)",
    color: "#6b3f09",
    lineHeight: 1.6,
  },
  buttonRow: {
    marginTop: 18,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  actions: {
    marginTop: 18,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 18px",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    textDecoration: "none",
  },
  secondaryLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 18px",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    textDecoration: "none",
  },
};

export default OwnerBookingsPage;